import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Bell, MagnifyingGlass, ArrowsClockwise, CheckCircle, WarningCircle, EnvelopeSimple, ClockCounterClockwise } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { listEmailComposeLogs } from '../services/api';
import { PageHero } from '../components/motion/PageHero';

const providerColor = (p) => ({
  brevo: 'bg-emerald-100 text-emerald-800',
  'resend-fallback': 'bg-blue-100 text-blue-800',
  resend: 'bg-blue-100 text-blue-800',
  mock: 'bg-gray-200 text-gray-700',
}[p] || 'bg-gray-100 text-gray-700');

const statusIcon = (s) => {
  if (s === 'sent') return <CheckCircle size={14} className="text-emerald-600" weight="fill" />;
  if (s === 'sent_mock') return <EnvelopeSimple size={14} className="text-gray-500" />;
  if (s === 'error') return <WarningCircle size={14} className="text-red-600" weight="fill" />;
  return <ClockCounterClockwise size={14} className="text-[#5E6D66]" />;
};

export default function NotificationsTimelinePage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ templates: [], providers: [], statuses: [] });
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterTemplate) params.template_name = filterTemplate;
      if (filterProvider) params.provider = filterProvider;
      if (filterStatus) params.status = filterStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (q) params.q = q;
      const res = await listEmailComposeLogs(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setFacets(res.data.facets || { templates: [], providers: [], statuses: [] });
    } catch {
      toast.error('Erreur chargement notifications');
    } finally {
      setLoading(false);
    }
  }, [q, filterTemplate, filterProvider, filterStatus, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-timeline-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Timeline notifications"
        subtitle="Chronologie complète des alertes envoyées"
        icon={<Bell size={24} weight="duotone" className="text-white" />}
        gradient="from-[#D5007D] to-[#EA3C3A]"
        accents={['#FF8E00', '#00AEEF']}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2421] tracking-tight flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            <Bell size={28} className="text-[#2D5A4C]" weight="fill" />
            Timeline notifications
          </h1>
          <p className="text-[#5E6D66] mt-1">Historique complet des emails envoyés (Brevo, Resend, mock)</p>
        </div>
        <Button variant="outline" onClick={load} className="border-[#E8E6E1]" data-testid="refresh-notifications-btn">
          <ArrowsClockwise size={16} className="mr-2" /> Actualiser
        </Button>
      </div>

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="relative col-span-2">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
            <Input
              placeholder="Rechercher sujet ou email..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 border-[#E8E6E1]"
              data-testid="filter-search"
            />
          </div>
          <Select value={filterTemplate || 'all'} onValueChange={(v) => setFilterTemplate(v === 'all' ? '' : v)}>
            <SelectTrigger className="border-[#E8E6E1]" data-testid="filter-template">
              <SelectValue placeholder="Template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous templates</SelectItem>
              {facets.templates.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.value} ({f.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterProvider || 'all'} onValueChange={(v) => setFilterProvider(v === 'all' ? '' : v)}>
            <SelectTrigger className="border-[#E8E6E1]" data-testid="filter-provider">
              <SelectValue placeholder="Provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous providers</SelectItem>
              {facets.providers.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.value} ({f.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="border-[#E8E6E1]" data-testid="filter-status">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {facets.statuses.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.value} ({f.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border-[#E8E6E1]" data-testid="filter-date-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border-[#E8E6E1]" data-testid="filter-date-to" />
        </CardContent>
      </Card>

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-0">
          {loading && <div className="p-6 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#5E6D66]" /></div>}
          {!loading && items.length === 0 && (
            <p className="text-center text-sm text-[#5E6D66] p-6">Aucune notification</p>
          )}
          {!loading && items.length > 0 && (
            <div className="divide-y divide-[#E8E6E1]" data-testid="notifications-list">
              <div className="px-4 py-2 bg-[#F9F8F5] text-xs text-[#5E6D66] flex items-center justify-between">
                <span>{items.length} affichée{items.length > 1 ? 's' : ''} sur {total}</span>
              </div>
              {items.map((log) => (
                <div key={log.id} className="p-4 hover:bg-[#F9F8F5] transition-colors" data-testid={`notification-${log.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="mt-1">{statusIcon(log.status)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-[#1A2421] truncate">{log.subject || '—'}</p>
                        <Badge className={`${providerColor(log.provider)} text-[10px]`}>{log.provider || '—'}</Badge>
                        {log.template && <Badge variant="outline" className="text-[10px]">{log.template}</Badge>}
                        {log.template_id && <Badge variant="outline" className="text-[10px]">tpl#{log.template_id}</Badge>}
                      </div>
                      <p className="text-xs text-[#5E6D66] mt-1">
                        Vers : <span className="font-mono">{Array.isArray(log.to) ? log.to.join(', ') : log.to}</span>
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-[#5E6D66]">
                        <span>{log.sent_at ? new Date(log.sent_at).toLocaleString('fr-FR') : ''}</span>
                        {log.message_id && <span className="font-mono truncate max-w-[240px]">{log.message_id}</span>}
                      </div>
                      {log.error && (
                        <p className="text-xs text-red-600 mt-1">{log.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
